import { useState, useRef, useEffect } from 'react';
import { useWorkspace, Workspace } from '../hooks/useWorkspace';
import { useWorkspaceLimit } from '../hooks/useWorkspaceLimit';
import { WorkspacePaywall } from './upgrade';
import { trackWorkspaceSwitched } from '../lib/analytics';

interface WorkspaceSelectorProps {
  onCreateNew?: () => void;
}

export default function WorkspaceSelector({ onCreateNew }: WorkspaceSelectorProps) {
  const { workspaces, currentWorkspace, setCurrentWorkspace, isLoading } = useWorkspace();
  const { canCreateWorkspace, plan, currentCount, limit } = useWorkspaceLimit();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (workspace: Workspace) => {
    setCurrentWorkspace(workspace);
    trackWorkspaceSwitched();
    setIsOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg">
        <div className="w-6 h-6 bg-gray-200 rounded animate-pulse" />
        <div className="w-24 h-4 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <button
        onClick={onCreateNew}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        <span>Create Workspace</span>
      </button>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-w-[180px]"
      >
        {/* Workspace avatar/icon */}
        <div className="w-6 h-6 bg-primary-100 rounded flex items-center justify-center flex-shrink-0">
          {currentWorkspace.logoUrl ? (
            <img
              src={currentWorkspace.logoUrl}
              alt={currentWorkspace.name}
              className="w-6 h-6 rounded object-cover"
            />
          ) : (
            <span className="text-xs font-semibold text-primary-600">
              {currentWorkspace.name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        {/* Workspace name */}
        <span className="flex-1 text-left truncate">{currentWorkspace.name}</span>

        {/* Stripe connection indicator */}
        {currentWorkspace.stripeConnected && (
          <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" title="Stripe connected" />
        )}

        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {/* Workspaces list */}
          <div className="max-h-64 overflow-y-auto">
            {workspaces.map((workspace) => (
              <button
                key={workspace.workspaceId}
                onClick={() => handleSelect(workspace)}
                className={`w-full flex items-center space-x-3 px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                  workspace.workspaceId === currentWorkspace.workspaceId ? 'bg-primary-50' : ''
                }`}
              >
                {/* Workspace avatar */}
                <div className="w-8 h-8 bg-primary-100 rounded flex items-center justify-center flex-shrink-0">
                  {workspace.logoUrl ? (
                    <img
                      src={workspace.logoUrl}
                      alt={workspace.name}
                      className="w-8 h-8 rounded object-cover"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-primary-600">
                      {workspace.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Workspace info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900 truncate">{workspace.name}</span>
                    {workspace.stripeConnected && (
                      <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" title="Stripe connected" />
                    )}
                  </div>
                  {workspace.description && (
                    <p className="text-xs text-gray-500 truncate">{workspace.description}</p>
                  )}
                </div>

                {/* Selected indicator */}
                {workspace.workspaceId === currentWorkspace.workspaceId && (
                  <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 my-1" />

          {/* Create new workspace or show limit info */}
          {canCreateWorkspace ? (
            <button
              onClick={() => {
                setIsOpen(false);
                onCreateNew?.();
              }}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 transition-colors"
            >
              <div className="w-8 h-8 bg-primary-50 rounded flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="font-medium">Create new workspace</span>
            </button>
          ) : (
            <WorkspacePaywall plan={plan} currentCount={currentCount} limit={limit} />
          )}
        </div>
      )}
    </div>
  );
}
